/**
 * PlayerInfoBar Component for Poker Reborn
 *
 * Compact info panel displaying player name and chip stack with
 * a dark semi-transparent background. Auto-sizes based on content
 * with min/max width constraints.
 *
 * @see docs/screens/room/PLAYER_INFO_BAR.md - Visual specs, dimensions
 * @see docs/screens/room/SEAT.md - Parent SeatView component
 */

import { Container, Graphics, Text } from 'pixi.js';
import { getScaledValue } from '@/utils/responsive';
import { PlayerChipsDisplay } from './PlayerChipsDisplay';

// =============================================================================
// BREAKPOINTS
// =============================================================================

/**
 * BBS breakpoints for PlayerInfoBar dimensions.
 * Uses discrete mode (values jump at breakpoints).
 *
 * Breakpoints: 375 (phone), 720 (tablet), 1440 (desktop), 1920 (large desktop)
 */
const PLAYER_INFO_BAR_BREAKPOINTS = {
  // Width constraints
  minWidth: { 375: 50, 720: 70, 1440: 100, 1920: 110 },
  maxWidth: { 375: 120, 720: 140, 1440: 160, 1920: 180 },

  // Padding
  paddingX: { 375: 8, 720: 10, 1440: 12, 1920: 14 },
  paddingY: { 375: 2, 720: 3, 1440: 3, 1920: 8 },

  // Gap between name and chips
  gap: { 375: 2, 720: 3, 1440: 4, 1920: 4 },

  // Corner radius
  cornerRadius: { 375: 6, 720: 8, 1440: 10, 1920: 12 },

  // Font sizes
  nameFont: { 375: 12, 720: 14, 1440: 16, 1920: 18 },
  stackFont: { 375: 12, 720: 14, 1440: 16, 1920: 18 },
};

// =============================================================================
// STYLES
// =============================================================================

const COLORS = {
  background: 0x000000,
  backgroundAlpha: 0.6,
  nameText: 0xffffff,
  stackText: 0xffd700, // Gold
};

// =============================================================================
// TYPES
// =============================================================================

export interface PlayerInfoBarOptions {
  /** Initial screen width for BBS calculations */
  screenWidth?: number;
}

// =============================================================================
// COMPONENT
// =============================================================================

export class PlayerInfoBar extends Container {
  private background: Graphics;
  private nameLabel: Text;
  private chipsDisplay: PlayerChipsDisplay;
  private betChipsContainer: Container;

  private currentScreenWidth: number;
  private currentName = '';

  // Cached layout geometry (exposed via getGeometry for TimerBorder)
  private cachedWidth = 0;
  private cachedHeight = 0;
  private cachedCornerRadius = 0;

  constructor(options: PlayerInfoBarOptions = {}) {
    super();

    this.currentScreenWidth = options.screenWidth ?? 1920;

    // Create background
    this.background = new Graphics();
    this.background.label = 'infoBarBg';
    this.addChild(this.background);

    // Create name label
    this.nameLabel = new Text({
      text: '',
      style: {
        fill: COLORS.nameText,
        fontSize: this.bbs(PLAYER_INFO_BAR_BREAKPOINTS.nameFont),
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: '500',
        align: 'center',
      },
    });
    this.nameLabel.anchor.set(0.5, 0);
    this.addChild(this.nameLabel);

    // Create chips display
    this.chipsDisplay = new PlayerChipsDisplay();
    this.chipsDisplay.resize(this.currentScreenWidth);
    this.addChild(this.chipsDisplay);

    // Create bet chips container (anchor for chip animations, centered)
    this.betChipsContainer = new Container();
    this.betChipsContainer.label = 'betChips';
    this.addChild(this.betChipsContainer);

    // Initial render (empty)
    this.updateLayout();
  }

  // ===========================================================================
  // BBS HELPER
  // ===========================================================================

  /**
   * Get a BBS value for current screen width (discrete mode).
   */
  private bbs(breakpoints: Record<number, number>): number {
    return getScaledValue(this.currentScreenWidth, breakpoints, { discrete: true });
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /**
   * Update player name.
   */
  setName(name: string): void {
    if (this.currentName === name) return;
    this.currentName = name;
    this.nameLabel.text = name;
    this.updateLayout();
  }

  /**
   * Update chip count with animation.
   */
  setChips(amount: number, animate = true): void {
    this.chipsDisplay.setChips(amount, animate);
    // Layout update happens after chips display updates its text
    // Use a small delay to ensure text is measured correctly
    requestAnimationFrame(() => { this.updateLayout(); });
  }

  /**
   * Set chip count immediately without animation.
   */
  setChipsImmediate(amount: number): void {
    this.chipsDisplay.setChips(amount, false);
    this.updateLayout();
  }

  /**
   * Resize for new screen width.
   */
  resize(screenWidth: number): void {
    if (screenWidth === this.currentScreenWidth) return;
    this.currentScreenWidth = screenWidth;

    // Update font sizes
    this.nameLabel.style.fontSize = this.bbs(PLAYER_INFO_BAR_BREAKPOINTS.nameFont);

    // Update chips display
    this.chipsDisplay.resize(screenWidth);

    // Recalculate layout
    this.updateLayout();
  }

  /**
   * Get the PlayerChipsDisplay for direct access (e.g., winner animation).
   */
  getChipsDisplay(): PlayerChipsDisplay {
    return this.chipsDisplay;
  }

  /**
   * Get bet chips container (anchor for chip animations).
   * Positioned at center of PlayerInfoBar.
   */
  getBetChipsContainer(): Container {
    return this.betChipsContainer;
  }

  /**
   * Get cached layout geometry (for TimerBorder to trace the perimeter).
   */
  getGeometry(): { width: number; height: number; cornerRadius: number } {
    return {
      width: this.cachedWidth,
      height: this.cachedHeight,
      cornerRadius: this.cachedCornerRadius,
    };
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  /**
   * Recalculate layout and redraw background.
   */
  private updateLayout(): void {
    const paddingX = this.bbs(PLAYER_INFO_BAR_BREAKPOINTS.paddingX);
    const paddingY = this.bbs(PLAYER_INFO_BAR_BREAKPOINTS.paddingY);
    const gap = this.bbs(PLAYER_INFO_BAR_BREAKPOINTS.gap);
    const minWidth = this.bbs(PLAYER_INFO_BAR_BREAKPOINTS.minWidth);
    const maxWidth = this.bbs(PLAYER_INFO_BAR_BREAKPOINTS.maxWidth);
    const cornerRadius = this.bbs(PLAYER_INFO_BAR_BREAKPOINTS.cornerRadius);

    // Measure content width
    const nameWidth = this.nameLabel.width;
    const chipsWidth = this.chipsDisplay.width;
    const contentWidth = Math.max(nameWidth, chipsWidth);

    // Calculate final width with constraints
    const desiredWidth = contentWidth + paddingX * 2;
    const finalWidth = Math.max(minWidth, Math.min(maxWidth, desiredWidth));

    // Calculate height
    const nameHeight = this.nameLabel.height;
    const chipsHeight = this.chipsDisplay.height;
    const contentHeight = nameHeight + gap + chipsHeight;
    const finalHeight = contentHeight + paddingY * 2;

    // Cache geometry for TimerBorder
    this.cachedWidth = finalWidth;
    this.cachedHeight = finalHeight;
    this.cachedCornerRadius = cornerRadius;

    // Draw background (centered at origin)
    this.background.clear();
    this.background.roundRect(
      -finalWidth / 2,
      -finalHeight / 2,
      finalWidth,
      finalHeight,
      cornerRadius
    );
    this.background.fill({ color: COLORS.background, alpha: COLORS.backgroundAlpha });

    // Position name label (centered horizontally, top of content area)
    this.nameLabel.x = 0;
    this.nameLabel.y = -finalHeight / 2 + paddingY;

    // Position chips display (centered horizontally, below name)
    this.chipsDisplay.x = 0;
    this.chipsDisplay.y = this.nameLabel.y + nameHeight + gap;
  }
}
